import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import styles from './EventDetails.module.scss';
import BlackCheckMarkIcon from 'app/assets/svgIcons/BlackCheckMarkIcon';
import { updateMoment } from '../RsdForms/api';
import { DropdownChevronIcon } from './DropdownChevronIcon';
import { ToastQueue } from '@adobe/react-spectrum';
import { useHomeSlice } from 'app/pages/Home/slice';
import { momentTypeSupportsGenAi } from 'utils/constants';

interface EventDetailsProps {
  momentType?: string;
  surfaces?: string[];
  genAI?: boolean;
  issueId: string;
  isDateClicked?: boolean;
  wholeCard?: any;
  /** View-only release / RTM / EOL phase name pill (e.g. Public Beta, VIP, Initial Announcement). */
  phaseLabel?: string;
  isGenAIDropdownOpen?: boolean; // ✅ Controlled state from parent
  onGenAIDropdownToggle?: () => void; // ✅ Callback to toggle dropdown
}

export const EventDetails: React.FC<EventDetailsProps> = ({
  momentType,
  surfaces,
  genAI = false,
  issueId,
  isDateClicked = false,
  wholeCard,
  phaseLabel,
  isGenAIDropdownOpen: externalGenAIDropdownOpen, // ✅ Controlled state from parent
  onGenAIDropdownToggle // ✅ Callback to toggle dropdown
}) => {
  const [localGenAI, setLocalGenAI] = useState(genAI);
  // ✅ Use controlled state if provided, otherwise use internal state
  const [internalGenAIDropdownOpen, setInternalGenAIDropdownOpen] =
    useState(false);
  const genAIDropdownOpen =
    externalGenAIDropdownOpen !== undefined
      ? externalGenAIDropdownOpen
      : internalGenAIDropdownOpen;

  const dispatch = useDispatch();
  const { actions } = useHomeSlice();

  // Sync local state with props when they change
  useEffect(() => {
    setLocalGenAI(genAI);
  }, [genAI]);

  const showGenAiControls = momentTypeSupportsGenAi(momentType, {
    pco: wholeCard?.pco
  });

  // Close dropdown on outside click
  useEffect(() => {
    if (!showGenAiControls) {
      return;
    }
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest('[data-dropdown="genai-tooltip"]')) {
        if (onGenAIDropdownToggle && genAIDropdownOpen) {
          onGenAIDropdownToggle(); // Close via parent if controlled
        } else {
          setInternalGenAIDropdownOpen(false); // Close internal state if not controlled
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showGenAiControls, genAIDropdownOpen, onGenAIDropdownToggle]);

  // Handle GenAI change
  const handleGenAIChange = async (value: boolean) => {
    if (!issueId) return;

    const oldGenAI = genAI;

    setLocalGenAI(value);

    // Close dropdown - use parent callback if controlled
    if (onGenAIDropdownToggle && genAIDropdownOpen) {
      onGenAIDropdownToggle();
    } else {
      setInternalGenAIDropdownOpen(false);
    }

    try {
      await updateMoment(issueId, { genai: value });

      // Show success message
      ToastQueue.positive('Card updated successfully', {
        timeout: 3000
      });

      // Silent refresh - fetches data in background without blocking UI or showing loader
      dispatch(actions.loadReleaseDataSilent({}));
    } catch (error) {
      setLocalGenAI(oldGenAI);
      console.error('Failed to update GenAI:', error);
      ToastQueue.negative('Failed to update GenAI. Please try again.', {
        timeout: 5000
      });
    }
  };

  // Handle dropdown click
  const handleDropdownClick = () => {
    if (isDateClicked) {
      if (onGenAIDropdownToggle) {
        onGenAIDropdownToggle(); // Use parent callback if controlled
      } else {
        setInternalGenAIDropdownOpen(prev => !prev); // Toggle internal state if not controlled
      }
    }
  };

  return (
    <div className={styles.eventDetailsContainer}>
      {momentType && (
        <div className={styles.eventDetailItem}>
          <span className={styles.eventDetailValue}>{momentType}</span>
        </div>
      )}
      {surfaces && surfaces.length > 0 && (
        <div className={`${styles.eventDetailItem} ${styles.surfacesItem}`}>
          <span className={styles.eventDetailValue}>{surfaces.join(', ')}</span>
        </div>
      )}

      {phaseLabel?.trim() && (
        <div className={styles.eventDetailItem}>
          <span className={styles.eventDetailValue}>{phaseLabel.trim()}</span>
        </div>
      )}

      {/* GenAI — not for Price Changes / Offers or PCO-flagged legacy-type moments */}
      {showGenAiControls && (
        <div style={{ position: 'relative' }} data-dropdown="genai-tooltip">
          <div
            className={styles.dropdownPill}
            style={{
              cursor: isDateClicked ? 'pointer' : 'default'
            }}
            onClick={handleDropdownClick}
          >
            <span className={styles.dropdownPillText}>
              GenAI: {localGenAI ? 'Yes' : 'No'}
            </span>
            {isDateClicked && <DropdownChevronIcon />}
          </div>

          {/* GenAI Dropdown Menu */}
          {genAIDropdownOpen && isDateClicked && (
            <div className={styles.dropdownMenu}>
              <div
                className={styles.dropdownItem}
                onClick={() => handleGenAIChange(true)}
              >
                <span className={styles.dropdownItemText}>Yes</span>
                {localGenAI && <BlackCheckMarkIcon width={18} height={14} />}
              </div>
              <div
                className={styles.dropdownItem}
                onClick={() => handleGenAIChange(false)}
              >
                <span className={styles.dropdownItemText}>No</span>
                {!localGenAI && <BlackCheckMarkIcon width={18} height={14} />}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
